import React, { useMemo } from "react";
import { Text, useMantineTheme } from "@mantine/core";
import { ResponsiveBar } from "@nivo/bar";
import _ from "lodash";
import { FormSectionCard } from "../../layout/page";

const defaultChartProps = {
  margin: { top: 50, right: 60, bottom: 80, left: 60 },
  padding: 0.3,
  valueScale: { type: "linear" },
  indexScale: { type: "band", round: true },
  groupMode: "grouped",
  enableLabel: true,
  labelSkipWidth: 12,
  labelSkipHeight: 12,
  legends: [
    {
      dataFrom: "keys",
      anchor: "bottom",
      direction: "row",
      justify: false,
      translateY: 60,
      itemsSpacing: 16,
      itemWidth: 100,
      itemHeight: 20,
      itemDirection: "left-to-right",
      itemOpacity: 0.85,
      symbolSize: 12,
    },
  ],
};

const LineOverlayLayer = ({
  bars,
  innerHeight,
  lineKey,
  lineBarKey,
  lineColor = "#1864ab",
  showLineLabels = false,
  formatLineLabel,
}) => {
  if (!bars?.length || !lineKey) return null;

  const segmentBars = lineBarKey
    ? bars.filter((bar) => bar.data.id === lineBarKey)
    : bars;

  if (!segmentBars.length) return null;

  const lineValues = segmentBars.map((bar) =>
    Number(bar.data.data[lineKey] ?? 0)
  );
  const maxLineValue = lineBarKey ? 100 : Math.max(...lineValues, 1);
  const lineTop = 20;

  const points = segmentBars.map((bar, index) => {
    if (lineBarKey) {
      return {
        x: bar.x + bar.width / 2,
        y: bar.y,
        value: lineValues[index],
      };
    }

    return {
      x: bar.x + bar.width / 2,
      y:
        innerHeight -
        lineTop -
        (lineValues[index] / maxLineValue) * (innerHeight - lineTop * 2),
      value: lineValues[index],
    };
  });

  const linePath = points
    .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
    .join(" ");

  return (
    <g>
      <path
        d={linePath}
        fill="none"
        stroke={lineColor}
        strokeWidth={2}
        pointerEvents="none"
      />
      {points.map((point, index) => (
        <g key={index}>
          <circle
            cx={point.x}
            cy={point.y}
            r={4}
            fill={lineColor}
            stroke="#fff"
            strokeWidth={1}
            pointerEvents="none"
          />
          {showLineLabels ? (
            <text
              x={point.x}
              y={point.y - 10}
              textAnchor="middle"
              fontSize="10"
              fill="currentColor"
              pointerEvents="none"
            >
              {formatLineLabel
                ? formatLineLabel(point.value)
                : `${point.value}%`}
            </text>
          ) : null}
        </g>
      ))}
    </g>
  );
};

const TopBarLabelLayer = ({ bars, labelKey, formatTopLabel }) => {
  if (!bars?.length || !labelKey) return null;

  const topBarsByIndex = bars.reduce((acc, bar) => {
    const index = bar.data.indexValue;
    if (!acc[index] || bar.y < acc[index].y) {
      acc[index] = bar;
    }
    return acc;
  }, {});

  return (
    <g>
      {Object.values(topBarsByIndex).map((bar) => {
        const value = bar.data.data[labelKey];
        return (
          <text
            key={String(bar.data.indexValue)}
            x={bar.x + bar.width / 2}
            y={bar.y - 8}
            textAnchor="middle"
            fontSize="10"
            fill="currentColor"
            pointerEvents="none"
          >
            {formatTopLabel ? formatTopLabel(value) : value}
          </text>
        );
      })}
    </g>
  );
};

const ComboBarLineChart = ({
  title,
  height = 400,
  description,
  data = [],
  barKey = "grossPremium",
  barKeys,
  barColors,
  lineKey,
  lineBarKey,
  lineColor = "#1864ab",
  stacked = false,
  showLineLabels = false,
  formatLineLabel,
  topLabelKey,
  formatTopLabel,
  indexBy = "name",
  chartProps = {},
}) => {
  const theme = useMantineTheme();

  const chartData = useMemo(
    () =>
      (data || []).map((item) => ({
        ...item,
        [indexBy]: item[indexBy] ?? item.name,
      })),
    [data, indexBy]
  );

  const resolvedBarKeys = barKeys?.length ? barKeys : [barKey];

  const chartTheme = {
    textColor:
      theme.colorScheme === "dark"
        ? theme.colors.gray[1]
        : theme.colors.dark[8],
    grid: {
      line: {
        stroke:
          theme.colorScheme === "dark"
            ? theme.colors.gray[8]
            : theme.colors.gray[1],
      },
    },
  };

  const mergedProps = _.merge(
    {
      ...defaultChartProps,
      data: chartData,
      keys: resolvedBarKeys,
      indexBy,
      groupMode: stacked ? "stacked" : "grouped",
      enableLabel: !stacked,
      colors:
        barColors ||
        (resolvedBarKeys.length > 1
          ? ["#ced4da", "#1971c2"]
          : ["#1864ab", "#1971c2", "#1c7ed6", "#339af0", "#74c0fc"]),
      axisBottom: {
        tickRotation: -25,
        format: (value) =>
          value?.length > 12 ? `${value.substring(0, 12)}...` : value,
      },
      layers: [
        "grid",
        "axes",
        "bars",
        ...(lineKey
          ? [
              (layerProps) => (
                <LineOverlayLayer
                  key="line-overlay"
                  {...layerProps}
                  lineKey={lineKey}
                  lineBarKey={lineBarKey}
                  lineColor={lineColor}
                  showLineLabels={showLineLabels}
                  formatLineLabel={formatLineLabel}
                />
              ),
            ]
          : []),
        ...(topLabelKey
          ? [
              (layerProps) => (
                <TopBarLabelLayer
                  key="top-bar-labels"
                  {...layerProps}
                  labelKey={topLabelKey}
                  formatTopLabel={formatTopLabel}
                />
              ),
            ]
          : []),
        "markers",
        "legends",
      ],
    },
    chartProps
  );

  return (
    <FormSectionCard title={title} h={height} w="100%" p="md">
      {description && (
        <Text size="xs" color="dimmed" mb="xs">
          {description}
        </Text>
      )}
      {!_.isEmpty(chartData) && (
        <ResponsiveBar theme={chartTheme} {...mergedProps} />
      )}
    </FormSectionCard>
  );
};

export default ComboBarLineChart;
